/**
 * ShareGridModal
 *
 * Admin-facing modal that surfaces a portal-login URL the recipient can
 * use to land on the client's planning grid. No token is minted here —
 * the URL goes through `/portal/login?redirect=…`, which sends the
 * recipient through the standard email magic-link flow.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { copyToClipboard } from '@/utils/clipboard';
import { buildClientPortalUrl } from '@/utils/clientPortalUrl';

interface ShareGridModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  clientName: string;
  gridName: string;
}

export const ShareGridModal: React.FC<ShareGridModalProps> = ({
  isOpen,
  onClose,
  clientId,
  clientName,
  gridName,
}) => {
  const [copied, setCopied] = useState(false);

  const portalUrl = useMemo(
    () =>
      buildClientPortalUrl({
        redirectPath: `/client/${clientId}/planning`,
      }),
    [clientId]
  );

  useEffect(() => {
    if (!isOpen) return;
    setCopied(false);
  }, [isOpen]);

  const handleCopy = async () => {
    await copyToClipboard(portalUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    setCopied(false);
    onClose();
  };

  const footerContent = (
    <Button onClick={handleClose} variant="ghost">
      Close
    </Button>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Share Grid: ${gridName}`}
      size="md"
      footer={footerContent}
    >
      <div className="p-6">
        <div className="space-y-6">
          {/* Success Banner */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center shrink-0">
                <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-green-900 text-sm">Portal Link</h3>
                <p className="text-green-700 text-sm mt-1">
                  Share this link with <span className="font-medium">{clientName}</span> to land them on the planning grid after sign-in.
                </p>
              </div>
            </div>
          </div>

          {/* Link Input */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Direct Link to Grid
            </label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={portalUrl}
                readOnly
                className="flex-1 p-2 bg-gray-50 border border-gray-200 rounded-sm text-sm font-mono text-gray-600 truncate"
              />
              <button
                onClick={handleCopy}
                className={`px-3 py-2 text-xs font-medium rounded transition-colors whitespace-nowrap ${
                  copied
                    ? 'bg-green-100 text-green-700 border border-green-200'
                    : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <p className="text-xs text-gray-500">
              Reusable link. Recipient authenticates via their own email.
            </p>
          </div>
        </div>
      </div>
    </Modal>
  );
};
